import DevSettingsService, {IDevBillingSettingDto} from "../../../services/api/dev-settings-service";
import {makeAutoObservable, runInAction} from "mobx";
import {IChangeable, ISaveable} from "../../../stores/types";
import {rootContainer, TYPE} from "../../../services/container/root";
import {IDateRangeISO} from "../../../components/date-range-picker-v2/date-range-picker-v2";

class DevBillingSettingsStore implements ISaveable, IChangeable<IDevBillingSettingDto> {

    private _model: IDevBillingSettingDto = {};
    private _changes: IDevBillingSettingDto = {};
    
    private _calculateBillingDate?: IDateRangeISO;

    private _isSaving: boolean = false;
    private _isLoading: boolean = false;
    private _isCalculating: boolean = false;


    constructor() {
        makeAutoObservable(this);
    }

    protected get service(): DevSettingsService {
        return rootContainer.get<DevSettingsService>(TYPE.DevSettingsService);
    }

    private _setModel(model: IDevBillingSettingDto) {
        this._changes = {};
        this._model = model;
    }

    getUpdatedModel() { return {...this._model, ...this._changes}; }
    isSaving(): boolean { return this._isSaving; }
    isLoading() { return this._isLoading; }
    isCalculating(): boolean { return this._isCalculating; }

    discardChanges(): void { this._changes = {}; }
    hasChanges(): boolean { return Object.keys(this._changes).length > 0; }

    public get calculateBillingDate() { return this._calculateBillingDate; }
    public setCalculateBillingDate(value: IDateRangeISO | null | undefined) { 
        this._calculateBillingDate = value ?? undefined; 
    }
    
    /**
     * Returns ISO UTC object for the current billing date setting.
     *
     * @returns {IDateRangeISO | undefined} ISO UTC date range object (single day: 00:00:00 to 23:59:59 UTC) or undefined if not set.
     * @example { from: "2025-01-15T00:00:00.000Z", to: "2025-01-15T23:59:59.999Z" }
     */
    public get currentBillingDate(): IDateRangeISO | undefined {
        if (this._changes._deleted) {
            return undefined;
        }
        const dateString = this._changes.current_day_for_billing ?? this._model.current_day_for_billing;
        if (!dateString) {
            return undefined;
        }
        
        /** Convert M/D/YYYY format from backend to ISO UTC single day range */
        const date = new Date(dateString);
        if (isNaN(date.getTime())) {
            return undefined;
        }
        
        /** Create single day range in UTC (00:00:00 to 23:59:59) */
        const startOfDay = new Date(Date.UTC(date.getFullYear(), date.getMonth(), date.getDate(), 0, 0, 0, 0));
        const endOfDay = new Date(Date.UTC(date.getFullYear(), date.getMonth(), date.getDate(), 23, 59, 59, 999));
        
        return {
            from: startOfDay.toISOString(),
            to: endOfDay.toISOString()
        };
    }

    /**
     * Sets current billing date from ISO UTC object.
     * @param {IDateRangeISO | null | undefined} value - ISO UTC date range object, null, or undefined to delete.
     * @example setCurrentBillingDate({ from: "2025-01-15T00:00:00.000Z", to: "2025-01-15T23:59:59.999Z" })
     */
    public setCurrentBillingDate(value: IDateRangeISO | null | undefined) {
        if (!value?.from) {
            this.setDeleted(true);
            this._changes.current_day_for_billing = undefined;
            return;
        }
        
        /** Parse ISO UTC "from" date and convert to M/D/YYYY for backend */
        const date = new Date(value.from);
        
        /** Backend expects M/D/YYYY format */
        this._changes.current_day_for_billing = `${date.getUTCMonth() + 1}/${date.getUTCDate()}/${date.getUTCFullYear()}`;
        this.setDeleted(false);
    }


    private setDeleted(v: boolean) {
        if (v) {
            this._changes._deleted = v;
            return;
        }
        delete this._changes._deleted;
    }

    public async load() {
        try {
            this._isLoading = true;
            const devBillingSettingsDTO = await this.service.fetchDevBillingSettings();
            runInAction(() => {
                this._setModel(devBillingSettingsDTO);
                this._isLoading = false;
            });
        }
        catch(e) {
            runInAction(() => {
                this._isLoading = false;
            });
        }
    }
    
    public async save(): Promise<void> {
        try {
            this._isSaving = true;
            const devBillingSettingsDTO = await this.service.updateBillingDevSettings(this.getUpdatedModel())
            runInAction(() => {
                this._setModel(devBillingSettingsDTO);
                this._isSaving = false;
            });
        }
        catch(e) {
            runInAction(() => {
                this._isSaving = false;
            });
        }
    }

    async runBillingCalculation() {
        if(!this._calculateBillingDate?.from) {
            return;
        }

        try {
            this._isCalculating = true;
            
            /** Parse ISO UTC "from" date and convert to locale date string for backend */
            const date = new Date(this._calculateBillingDate.from);
            const dateString = `${date.getUTCMonth() + 1}/${date.getUTCDate()}/${date.getUTCFullYear()}`;
            
            await this.service.runBillingCalculation(dateString);
        }
        catch(e) { }

        runInAction(() => {
            this._isCalculating = false;
        })
    }
}

export default new DevBillingSettingsStore();